import { useCallback, useSyncExternalStore } from "react";

import { isContextPath } from "src/components/layout/nav/sections/hooks/projects";
import { useUser } from "src/contexts/user-context";
import { getStoredValue, setStoredValue } from "src/hooks/use-persisted-state";

/** The full-screen settings' two sidebars, Settings' and Context's, which each open back up to their own last page. */
export type SettingsSidebar = "settings" | "context";

/** Per workspace, since each workspace has its own settings pages. */
export const lastSettingsPathKey = (
  workspaceId: string,
  sidebar: SettingsSidebar = "settings",
) => `${workspaceId}:${sidebar}:last-path`;

const sidebarForPath = (path: string): SettingsSidebar =>
  isContextPath(`/workspace${path.split(/[?#]/)[0]}`) ? "context" : "settings";

const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const useWorkspaceId = () => {
  const { workspace } = useUser();
  return workspace?.id == undefined ? null : String(workspace.id);
};

/** The workspace path, slug excluded, the viewer last had open under the sidebar, or null before they open any. */
export const useLastSettingsPath = (
  sidebar: SettingsSidebar = "settings",
): string | null => {
  const workspaceId = useWorkspaceId();
  const key = workspaceId && lastSettingsPathKey(workspaceId, sidebar);
  return useSyncExternalStore(subscribe, () => {
    if (!key) return null;
    const path = getStoredValue<unknown>(key, null);
    return typeof path === "string" ? path : null;
  });
};

/** Records the settings page the viewer is on, so the nav's Settings, or Context for its pages, opens back up to it. */
export const useRememberSettingsPath = () => {
  const workspaceId = useWorkspaceId();
  return useCallback(
    (path: string) => {
      if (!workspaceId) return;
      const key = lastSettingsPathKey(workspaceId, sidebarForPath(path));
      if (getStoredValue<unknown>(key, null) === path) return;
      setStoredValue(key, path);
      for (const listener of listeners) listener();
    },
    [workspaceId],
  );
};
